// https://skyscanner-prod-sttc-int-eu-west-1.s3.amazonaws.com/sttc/schemadocs/index.html#autosuggest.Telemetry
import { RESULT_TYPE } from './constants';

import type { AutosuggestSuggestion } from '../types';
import type { Culture } from '@shared/types/context';
import type { Maybe, ObjectMapping } from '@shared/types/utils';

import { AUTO_SUGGEST_POI_LIST } from '@shared/utils-frontend/mini-events/constants';

type QueryType = {
  prefilledQuery: string;
  isRecentIncluded: boolean;
};

export const buildAdditionalInfoAutoSuggestPois = (
  suggestion: AutosuggestSuggestion,
) => {
  let poi;
  const poiList = AUTO_SUGGEST_POI_LIST.reduce(
    (acc: any, curr: string, index: number) => {
      poi = (suggestion.pois && suggestion.pois[index]) || {};
      acc[curr] = poi.entityId || '';
      return acc;
    },
    {},
  );
  return {
    auto_suggest_pois: {
      city_id: suggestion.entityId,
      ...poiList,
    },
  };
};

export const buildAdditionalInfoAutoSuggestPoiClick = ({
  cityId,
  poiId,
}: {
  cityId: string;
  poiId: string;
}) => ({
  auto_suggest_poi_click: { city_id: cityId, poi_id: poiId },
});

const buildCultureSettings = (culture: Culture) => {
  if (!culture) {
    return {};
  }

  return {
    culture_settings: {
      country: culture.market,
      locale: culture.locale,
      currency: culture.currency,
    },
  };
};

const buildCommonTelemetryProps = (culture: Culture) => ({
  ...buildCultureSettings(culture),
  programme: 'HOTELS',
});

export const buildControlsActivatedMessage = (
  query: QueryType,
  culture: Culture,
) => ({
  ...buildCommonTelemetryProps(culture),
  controls_activated: {
    prefilled_query: query.prefilledQuery,
    is_recent_included: query.isRecentIncluded,
  },
});

export const buildResultSelectedMessage = (
  query: Maybe<string>,
  suggestionValue: string,
  suggestionIndex: number,
  entityId: string,
  culture: Culture,
  resultType = RESULT_TYPE.UNSET_RESULT_TYPE,
) => ({
  ...buildCommonTelemetryProps(culture),
  result_selected: {
    query,
    selected_entity_id: entityId,
    selected_value: suggestionValue,
    position: suggestionIndex + 1,
    selected_result_type: resultType,
  },
});

export const buildResultsUpdatedMessage = (
  query: Maybe<string>,
  numResults: number,
  culture: Culture,
  suggestedEntityIds: string,
) => ({
  ...buildCommonTelemetryProps(culture),
  results_updated: {
    query,
    num_results: numResults,
    suggested_entity_ids: suggestedEntityIds,
  },
});

export const buildControlsDismissedMessage = (
  query: Maybe<string>,
  culture: Culture,
) => ({
  ...buildCommonTelemetryProps(culture),
  controls_dismissed: {
    query,
  },
});

export const buildAdditionalInfoDayViewSearch = ({
  entityID,
  entityName,
  entityType,
  filters,
  isSearchWithChildren,
}: {
  entityID?: number;
  entityName: Maybe<string>;
  entityType: Maybe<string>;
  filters: Maybe<ObjectMapping>;
  isSearchWithChildren: boolean;
}) => ({
  day_view_search: {
    entity_id: entityID,
    entity_name: entityName,
    is_search_with_children: isSearchWithChildren,
    filters,
    entity_type: entityType,
  },
});
